// Outbox Service for Offline Sales Queue Management
// Uniqueness is (sale_id, store_db). Public sale_id stays offline-{pc}-{n}.
import { getNextInvoiceNumber } from './invoiceNumberService';
import { getStorage } from './storageAdapter';
import { errorHandler } from './errorHandler';

const INSERT_OUTBOX_SALE_SQL = `INSERT INTO outbox_sales
            (sale_id, date, status, upload_response, upload_date, payload, upload_invoice, attempts, last_attempt_date, store_db, client_meta)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

const normalizeStoreDb = (storeDB) => String(storeDB || '').trim();

function mapRowToItem(row) {
    return {
        saleId: row.sale_id,
        date: row.date,
        status: row.status,
        uploadResponse: row.upload_response ? JSON.parse(row.upload_response) : null,
        uploadDate: row.upload_date,
        payload: JSON.parse(row.payload),
        uploadInvoice: row.upload_invoice,
        attempts: row.attempts || 0,
        lastAttemptDate: row.last_attempt_date,
        storeDB: row.store_db,
        clientMeta: row.client_meta ? JSON.parse(row.client_meta) : {}
    };
}

function parseSaleNumber(item) {
    const raw = item?.payload?.saleNumber ?? item?.payload?.SaleNum ?? item?.payload?.orderNumber;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : Number.MAX_SAFE_INTEGER;
}

const isUniqueConstraint = (error) =>
    String(error?.message || '').toLowerCase().includes('unique constraint failed');

/**
 * @param {String} storeDB
 * @returns {Promise<{count: number, maxSaleNumber: number}>}
 */
export async function getPendingStatsForStore(storeDB) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_sales
             WHERE status IN ('pending', 'timeout', 'failed', 'sending')
             AND store_db = ?`,
            [storeDB || '']
        );
        const items = (rows || []).map(mapRowToItem);
        const maxSaleNumber = items.reduce((max, item) => {
            const n = parseSaleNumber(item);
            if (!Number.isFinite(n) || n === Number.MAX_SAFE_INTEGER) return max;
            return Math.max(max, n);
        }, 0);
        return { count: items.length, maxSaleNumber };
    } catch (error) {
        console.error('[Outbox] Failed to get pending stats:', error);
        return { count: 0, maxSaleNumber: 0 };
    }
}

/**
 * @param {Object} payload
 * @param {String} storeDB
 * @param {Object} clientMeta
 * @returns {Promise<String|null>}
 */
export async function enqueueSale(payload, storeDB, clientMeta = {}) {
    try {
        const storage = await getStorage();
        const storeKey = normalizeStoreDb(storeDB);
        const clientMetaWithTimestamp = {
            ...clientMeta,
            clientTimestamp: new Date().toISOString()
        };
        const nowIso = new Date().toISOString();

        for (let attempt = 0; attempt < 5; attempt++) {
            const saleId = await getNextInvoiceNumber(storeKey);
            try {
                await storage.run(
                    INSERT_OUTBOX_SALE_SQL,
                    [
                        saleId,
                        nowIso,
                        'pending',
                        null,
                        null,
                        JSON.stringify(payload),
                        null,
                        0,
                        null,
                        storeKey,
                        JSON.stringify(clientMetaWithTimestamp)
                    ]
                );

                console.log(`[Outbox] Enqueued sale: ${saleId} store=${storeKey}`);
                return saleId;
            } catch (insertErr) {
                if (!isUniqueConstraint(insertErr) || attempt === 4) {
                    throw insertErr;
                }
                console.warn(`[Outbox] sale_id collision on attempt ${attempt + 1} (${saleId}, ${storeKey}), retrying...`);
            }
        }

        return null;
    } catch (error) {
        console.error('[Outbox] Failed to enqueue sale:', error);
        return null;
    }
}

export async function getPending(batchSize = 10) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_sales
             WHERE status IN ('pending', 'timeout')
             ORDER BY date ASC
             LIMIT ?`,
            [batchSize]
        );
        return rows.map(mapRowToItem);
    } catch (error) {
        console.error('[Outbox] Failed to get pending sales:', error);
        return [];
    }
}

export async function getPendingOrdered(batchSize = 10) {
    const items = await getPending(500);
    const ordered = (items || []).sort((a, b) => {
        const aNum = parseSaleNumber(a);
        const bNum = parseSaleNumber(b);
        if (aNum !== bNum) return aNum - bNum;
        return new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime();
    });
    return ordered.slice(0, batchSize);
}

export async function markSending(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sending',
                     attempts = attempts + 1,
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [new Date().toISOString(), saleId, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sending',
                     attempts = attempts + 1,
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                [new Date().toISOString(), saleId]
            );
        }
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as sending:`, error);
    }
}

export async function markSent(saleId, serverMeta = {}, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        const params = [
            JSON.stringify(serverMeta),
            new Date().toISOString(),
            serverMeta.invoiceNo || serverMeta.invoice || null,
            saleId
        ];
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sent',
                     upload_response = ?,
                     upload_date = ?,
                     upload_invoice = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [...params, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET status = 'sent',
                     upload_response = ?,
                     upload_date = ?,
                     upload_invoice = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                params
            );
        }
        console.log(`[Outbox] Marked ${saleId} as sent. Invoice: ${serverMeta.invoiceNo || serverMeta.invoice}`);
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as sent:`, error);
    }
}

export async function markFailed(saleId, reason, permanent = false, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);

        const item = db
            ? await storage.get(
                `SELECT attempts FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            )
            : await storage.get(
                `SELECT attempts FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );

        if (item) {
            const maxAttempts = 5;
            const status = permanent || item.attempts >= maxAttempts ? 'failed' : 'timeout';

            const serverMessage =
                (typeof reason === 'string' && reason.trim()
                    ? errorHandler.toPlainErrorText(reason) || reason.trim()
                    : errorHandler.toPlainErrorText(reason)) ||
                (reason && typeof reason === 'object' && reason.message
                    ? String(reason.message)
                    : '') ||
                'Unknown error';

            const args = [
                status,
                JSON.stringify({
                    error: serverMessage,
                    failedAt: new Date().toISOString()
                }),
                new Date().toISOString(),
                saleId
            ];

            if (db) {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = ?,
                         upload_response = ?,
                         last_attempt_date = ?,
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ? AND store_db = ?`,
                    [...args, db]
                );
            } else {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = ?,
                         upload_response = ?,
                         last_attempt_date = ?,
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ?`,
                    args
                );
            }

            console.log(`[Outbox] Marked ${saleId} as ${status} (attempt ${item.attempts}${permanent ? ', permanent failure' : ''})`);
        }
    } catch (error) {
        console.error(`[Outbox] Failed to mark ${saleId} as failed:`, error);
    }
}

export async function incrementRetries(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        if (db) {
            await storage.run(
                `UPDATE outbox_sales
                 SET attempts = attempts + 1,
                     status = 'pending',
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ? AND store_db = ?`,
                [new Date().toISOString(), saleId, db]
            );
        } else {
            await storage.run(
                `UPDATE outbox_sales
                 SET attempts = attempts + 1,
                     status = 'pending',
                     last_attempt_date = ?,
                     updated_at = CURRENT_TIMESTAMP
                 WHERE sale_id = ?`,
                [new Date().toISOString(), saleId]
            );
        }
        console.log(`[Outbox] Retry ${saleId}`);
    } catch (error) {
        console.error(`[Outbox] Failed to increment retries for ${saleId}:`, error);
    }
}

export async function forceRetrySaleId(saleId, storeDb) {
    const storage = await getStorage();
    const db = normalizeStoreDb(storeDb);
    const row = db
        ? await storage.get(`SELECT * FROM outbox_sales WHERE sale_id = ? AND store_db = ?`, [saleId, db])
        : await storage.get(`SELECT * FROM outbox_sales WHERE sale_id = ?`, [saleId]);
    if (!row) throw new Error(`Outbox item ${saleId} not found`);

    const resolvedStore = normalizeStoreDb(storeDb || row.store_db);

    const clientMeta = row.client_meta
        ? (typeof row.client_meta === 'string' ? JSON.parse(row.client_meta) : row.client_meta)
        : {};

    const { generateUUID } = await import('../utils/uuid');

    const payload = typeof row.payload === 'string' ? JSON.parse(row.payload) : row.payload;
    const oldLocalId = payload.localId || null;
    const newLocalId = generateUUID();
    payload.localId = newLocalId;

    const forceCount = Number(clientMeta.forceLocalIdCount || 0) + 1;
    const updatedMeta = {
        ...clientMeta,
        forceLocalIdAt: new Date().toISOString(),
        forceLocalIdCount: forceCount,
        previousLocalId: oldLocalId
    };

    if (resolvedStore) {
        await storage.run(
            `UPDATE outbox_sales SET payload = ?, client_meta = ?, status = 'pending', attempts = 0, upload_response = NULL, upload_date = NULL WHERE sale_id = ? AND store_db = ?`,
            [JSON.stringify(payload), JSON.stringify(updatedMeta), saleId, resolvedStore]
        );
    } else {
        await storage.run(
            `UPDATE outbox_sales SET payload = ?, client_meta = ?, status = 'pending', attempts = 0, upload_response = NULL, upload_date = NULL WHERE sale_id = ?`,
            [JSON.stringify(payload), JSON.stringify(updatedMeta), saleId]
        );
    }

    console.log(`[Outbox] Force retry (#${forceCount}) localId: ${oldLocalId} -> ${newLocalId} for sale ${saleId}`);
    return { oldLocalId, newLocalId };
}

export async function countByStatus(status) {
    try {
        const storage = await getStorage();
        const result = await storage.get(
            `SELECT COUNT(*) as count FROM outbox_sales WHERE status = ?`,
            [status]
        );
        return result ? result.count : 0;
    } catch (error) {
        console.error(`[Outbox] Failed to count items by status ${status}:`, error);
        return 0;
    }
}

export async function getAllOutboxItems() {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT * FROM outbox_sales ORDER BY date DESC`
        );
        return rows.map(mapRowToItem);
    } catch (error) {
        console.error('[Outbox] Failed to get all items:', error);
        return [];
    }
}

export async function getOutboxItem(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        const row = db
            ? await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            )
            : await storage.get(
                `SELECT * FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );
        return row ? mapRowToItem(row) : undefined;
    } catch (error) {
        console.error(`[Outbox] Failed to get item ${saleId}:`, error);
        return undefined;
    }
}

export async function deleteOutboxItem(saleId, storeDb) {
    try {
        const storage = await getStorage();
        const db = normalizeStoreDb(storeDb);
        if (db) {
            await storage.run(
                `DELETE FROM outbox_sales WHERE sale_id = ? AND store_db = ?`,
                [saleId, db]
            );
        } else {
            await storage.run(
                `DELETE FROM outbox_sales WHERE sale_id = ?`,
                [saleId]
            );
        }
        console.log(`[Outbox] Deleted item: ${saleId}`);
    } catch (error) {
        console.error(`[Outbox] Failed to delete item ${saleId}:`, error);
    }
}

export async function cleanupSentItems(daysOld = 30) {
    try {
        const storage = await getStorage();
        const threshold = new Date();
        threshold.setDate(threshold.getDate() - daysOld);

        const result = await storage.run(
            `DELETE FROM outbox_sales
             WHERE status = 'sent' AND date < ?`,
            [threshold.toISOString()]
        );

        const deletedCount = result.changes || 0;
        console.log(`[Outbox] Cleaned up ${deletedCount} sent items older than ${daysOld} days`);
        return deletedCount;
    } catch (error) {
        console.error('[Outbox] Failed to cleanup sent items:', error);
        return 0;
    }
}

export async function resetStaleSending(staleMinutes = 10) {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT sale_id, store_db, last_attempt_date FROM outbox_sales WHERE status = 'sending'`
        );
        const cutoffMs = Math.max(1, staleMinutes) * 60 * 1000;
        const nowMs = Date.now();
        const stale = (rows || []).filter(row => {
            const t = row.last_attempt_date ? new Date(row.last_attempt_date).getTime() : 0;
            return !t || (nowMs - t) > cutoffMs;
        });
        for (const row of stale) {
            const db = normalizeStoreDb(row.store_db);
            if (db) {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = 'pending',
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ? AND store_db = ? AND status = 'sending'`,
                    [row.sale_id, db]
                );
            } else {
                await storage.run(
                    `UPDATE outbox_sales
                     SET status = 'pending',
                         updated_at = CURRENT_TIMESTAMP
                     WHERE sale_id = ? AND status = 'sending'`,
                    [row.sale_id]
                );
            }
        }
        if (stale.length) {
            console.log(`[Outbox] Reset ${stale.length} stale 'sending' row(s) to pending (staleMinutes=${staleMinutes})`);
        }
        return stale.length;
    } catch (error) {
        console.error('[Outbox] Failed to reset stale sending rows:', error);
        return 0;
    }
}

export async function countPending() {
    try {
        const storage = await getStorage();
        const rows = await storage.query(
            `SELECT COUNT(*) as count FROM outbox_sales
             WHERE status IN ('pending', 'timeout', 'failed', 'sending')`
        );
        if (Array.isArray(rows) && rows.length > 0) {
            return Number(rows[0].count) || 0;
        }
        if (rows && typeof rows === 'object' && 'count' in rows) {
            return Number(rows.count) || 0;
        }
        return 0;
    } catch (error) {
        console.error('[Outbox] Failed to count pending items:', error);
        return 0;
    }
}

export async function hasPendingSync() {
    return (await countPending()) > 0;
}

export default {
    enqueueSale,
    getPending,
    markSending,
    markSent,
    markFailed,
    incrementRetries,
    getPendingOrdered,
    getPendingStatsForStore,
    forceRetrySaleId,
    countByStatus,
    countPending,
    hasPendingSync,
    getAllOutboxItems,
    getOutboxItem,
    deleteOutboxItem,
    cleanupSentItems,
    resetStaleSending
};
